import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listContacts,
  createContact,
  updateContact,
  deleteContact,
  batchCreateContacts,
  logContactInteraction,
} from "@/api/companies";
import type { ContactInput } from "@career-ops-amplify/shared";

const CONTACTS_QUERY_KEY = ["contacts"] as const;

export function useContacts(filters?: {
  companySlug?: string;
  jobId?: string;
  role?: string;
  source?: string;
  search?: string;
  limit?: number;
  cursor?: string;
  offset?: number;
}) {
  return useQuery({
    queryKey: [...CONTACTS_QUERY_KEY, filters ?? {}],
    queryFn: () => listContacts(filters),
    staleTime: 30_000,
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContactInput) => createContact(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["companies"] });
    },
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<ContactInput> }) =>
      updateContact(id, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
    },
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteContact(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
    },
  });
}

export function useBatchCreateContacts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contacts: ContactInput[]) => batchCreateContacts(contacts),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
    },
  });
}

export function useLogContactInteraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      notes,
      channel,
    }: {
      id: string;
      notes?: string;
      channel?: string;
    }) => logContactInteraction(id, { notes, channel }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
    },
  });
}
